<template>
  <v-menu
    :nudge-width="100"
    offset-y
    min-width="350"
    max-height="600"
    :close-on-content-click="false"
  >
    <template #activator="{ props }">
      <v-btn icon v-bind="props" :class="{ 'notification-blink': isBlinking }">
        <v-badge
          v-if="unreadCount > 0"
          color="green"
          :content="unreadCount"
          overlap
          :class="{ 'badge-blink': isBlinking }"
        >
          <v-icon :class="{ 'icon-pulse': isBlinking }">mdi-bell</v-icon>
        </v-badge>
        <v-icon v-else :class="{ 'icon-pulse': isBlinking }">mdi-bell</v-icon>
      </v-btn>
    </template>
    <template #default>
      <v-card flat class="notification-menu">
        <v-list class="notification-list">
          <div v-if="loading" class="pa-4 text-center">
            <v-progress-circular indeterminate />
          </div>
          <template v-else>
            <template v-if="notificationsList.length > 0">
              <NotificationItem
                v-for="notification in notificationsList"
                :key="notification.id"
                :notification="notification"
                :last="true"
              />
            </template>
            <v-list-item v-else>
              <v-list-item-title class="text-center text-grey"
                >Geen notificaties</v-list-item-title
              >
            </v-list-item>
          </template>
        </v-list>

        <v-divider />
        <v-list-item
          variant="flat"
          class="mark-all-read"
          :disabled="markingAllRead || unreadCount === 0"
          :class="{ 'mark-all-read--disabled': unreadCount === 0 }"
          @click.stop="markAllRead"
        >
          <template #prepend>
            <v-icon
              :icon="
                markingAllRead
                  ? 'mdi-loading'
                  : 'mdi-email-open-multiple-outline'
              "
              class="mr-2"
              :class="{ rotate: markingAllRead }"
            />
          </template>
          <v-list-item-title>
            <template v-if="unreadCount > 0">
              Markeer alle notificaties ({{ unreadCount }}) als gelezen
            </template>
            <template v-else> Geen ongelezen notificaties </template>
          </v-list-item-title>
        </v-list-item>
      </v-card>
    </template>
  </v-menu>
</template>

<script>
import { computed, onMounted, ref, watch, nextTick } from "vue";
import NotificationItem from "./NotificationItem.vue";
import { useAuthStoreRefactor } from "../../stores/authStoreRefactor";
import { useNotificationsStoreRefactor } from "../../stores/notificationsStoreRefactor";
import { useAlertStore } from "../../stores/alertStore";

export default {
  name: "NotificationMenu",
  components: {
    NotificationItem,
  },
  setup() {
    const authStore = useAuthStoreRefactor();
    const notificationsStore = useNotificationsStoreRefactor();
    const alertStore = useAlertStore();

    const loading = ref(false);
    const markingAllRead = ref(false);
    const isBlinking = ref(false);

    // Computed properties
    const currentUser = computed(() => authStore.user);

    // Watch for changes in the notifications store's notifications array
    watch(
      () => notificationsStore.notifications,
      (newNotifications, oldNotifications) => {
        if (!newNotifications || !oldNotifications) return;

        // Only process if there's an actual change in the data
        if (
          newNotifications.length !== oldNotifications.length ||
          JSON.stringify(newNotifications.map((n) => n.id)) !==
            JSON.stringify(oldNotifications.map((n) => n.id))
        ) {
          console.log(
            "NotificationMenu: Notifications list changed significantly",
            {
              old: oldNotifications?.length,
              new: newNotifications?.length,
              oldIds: oldNotifications?.map((n) => n.id),
              newIds: newNotifications?.map((n) => n.id),
            },
          );

          // Force UI update by slightly delaying refresh
          nextTick(() => {
            console.log("NotificationMenu: Forcing UI refresh on nextTick");
            loading.value = true;
            setTimeout(() => {
              loading.value = false;
              console.log("NotificationMenu: UI refresh completed");
            }, 100);
          });
        } else {
          // Check if any notification properties have changed
          const changedNotifications = newNotifications.filter(
            (newNotif, index) => {
              const oldNotif = oldNotifications[index];
              if (!oldNotif) return true;
              return JSON.stringify(newNotif) !== JSON.stringify(oldNotif);
            },
          );

          if (changedNotifications.length > 0) {
            console.log(
              "NotificationMenu: Notification properties changed for",
              changedNotifications.length,
              "items",
            );
            // Force UI update for property changes
            nextTick(() => {
              console.log(
                "NotificationMenu: Forcing UI refresh for property changes",
              );
              loading.value = true;
              setTimeout(() => {
                loading.value = false;
                console.log("NotificationMenu: UI refresh completed");
              }, 100);
            });
          }
        }
      },
      { deep: true },
    );

    // Additional watcher to track getNotifications getter
    watch(
      () => notificationsStore.getNotifications,
      (newNotifications) => {
        console.log("NotificationMenu: getNotifications getter changed", {
          count: newNotifications?.length,
          ids: newNotifications?.map((n) => n.id),
        });
      },
      { deep: true },
    );

    // Watch the lastUpdated timestamp in the store
    watch(
      () => notificationsStore.lastUpdated,
      (newTimestamp, oldTimestamp) => {
        if (newTimestamp && (!oldTimestamp || newTimestamp > oldTimestamp)) {
          console.log("NotificationMenu: Store lastUpdated changed", {
            old: oldTimestamp ? new Date(oldTimestamp).toISOString() : null,
            new: newTimestamp ? new Date(newTimestamp).toISOString() : null,
          });

          // Refresh the notification list when the store updates
          nextTick(() => {
            console.log("NotificationMenu: Refreshing on lastUpdated change");
            // Force refresh by toggling loading state
            loading.value = true;
            setTimeout(() => {
              loading.value = false;
              console.log(
                "NotificationMenu: UI refresh completed after timestamp change",
              );
            }, 100);
          });
        }
      },
    );

    // This is particularly important for loading data in the notification menu
    watch(
      () => notificationsStore.loading,
      (isLoading) => {
        console.log(
          "NotificationMenu: Store loading state changed:",
          isLoading,
        );
        // Sync local loading state with store loading state
        loading.value = isLoading;
      },
    );

    const notificationsList = computed(() => {
      console.log("NotificationMenu: Re-computing notifications list");
      if (!currentUser.value) return [];

      // Use the store's getter instead of accessing state directly
      const allNotifications = notificationsStore.getNotifications;
      console.log("All notifications:", allNotifications);
      console.log("Current user:", currentUser.value);

      const filtered = allNotifications.filter((notification) => {
        console.log("Checking notification:", notification);
        return (
          notification.target_type === "User" &&
          notification.target_id === currentUser.value.id
        );
      });
      console.log("Filtered notifications:", filtered);

      return filtered.sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at),
      );
    });

    const unreadCount = computed(() => {
      const count = notificationsStore.getUnreadCount;
      console.log("Unread count:", count);
      return count;
    });

    const markAllRead = async () => {
      if (markingAllRead.value) return;

      try {
        markingAllRead.value = true;
        await notificationsStore.readAll();
        alertStore.setAlert(
          "Alle notificaties zijn als gelezen gemarkeerd",
          "success",
        );
      } catch (error) {
        console.error("Error marking notifications as read:", error);
        alertStore.setAlert(
          "Er is een fout opgetreden bij het markeren van notificaties",
          "error",
        );
      } finally {
        markingAllRead.value = false;
      }
    };

    // Load notifications on mount
    onMounted(async () => {
      // Only load if we don't have notifications already and we're not currently loading
      if (
        notificationsStore.notifications.length === 0 &&
        !notificationsStore.loading
      ) {
        try {
          loading.value = true;
          await notificationsStore.fetchAll();
        } catch (error) {
          console.error("Error loading notifications:", error);
          alertStore.setAlert(
            "Er is een fout opgetreden bij het laden van notificaties",
            "error",
          );
        } finally {
          loading.value = false;
        }
      } else {
        console.log(
          "NotificationMenu: Notifications already available, skipping load",
        );
      }
    });

    // Watch for new notifications and trigger blinking
    watch(
      () => notificationsStore.notifications.length,
      (newCount, oldCount) => {
        // Only trigger blinking if we're adding notifications (not initial load or removal)
        if (oldCount && newCount > oldCount) {
          console.log(
            "NotificationMenu: New notification detected, triggering blink animation",
          );
          isBlinking.value = true;

          // Stop blinking after 4 seconds
          setTimeout(() => {
            console.log("NotificationMenu: Stopping blink animation");
            isBlinking.value = false;
          }, 4000);
        }
      },
    );

    return {
      notificationsList,
      unreadCount,
      markAllRead,
      loading,
      markingAllRead,
      isBlinking,
    };
  },
};
</script>

<style scoped>
.notification-menu {
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.notification-list {
  max-height: 400px;
  overflow-y: auto;
}

.mark-all-read {
  background-color: rgb(var(--v-theme-primary-lighten-5));
  transition: all 0.2s ease;
}

.mark-all-read:not(.mark-all-read--disabled):hover {
  background-color: rgb(var(--v-theme-primary-lighten-3));
}

.mark-all-read--disabled {
  opacity: 0.7;
  background-color: rgb(var(--v-theme-primary-lighten-5));
}

.rotate {
  animation: rotate 1s linear infinite;
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* Notification blinking animations */
.badge-blink {
  animation: badge-blink 0.5s ease-in-out infinite;
}

.icon-pulse {
  animation: icon-grow 0.4s ease-in-out infinite;
}

@keyframes badge-blink {
  0%,
  100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.3;
    transform: scale(0.9);
  }
}

@keyframes icon-grow {
  0%,
  100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.15);
  }
}
</style>
